import { useMemo } from "react";
import {
  useFetchUserCalendarsQuery,
  userCalendarsQueryKeys,
} from "../../react-query/userCalendars/queries";
import { hasNoStartOrEnd } from "../../services/block/block.service";
import { BlockType } from "../../types/block/type";
import { UserCalendarAccessType } from "../../types/userCalendars/enum";
import { debounceInvalidate } from "../../services/optimistic-updates/debounceInvalidate";
import { calendarQueryKeys } from "../../react-query/calendar/queries";
import { inboxQueryKeys } from "../../react-query/inbox/queries";
import { useQueryClient } from "@tanstack/react-query";

const useBlockCalendarSync = () => {
  const queryClient = useQueryClient();
  const fetchUserCalendarsQuery = useFetchUserCalendarsQuery({
    calendarAccessType: UserCalendarAccessType.EDITABLE,
  });
  const primaryCalendar = useMemo(() => {
    return fetchUserCalendarsQuery.data?.find((calendar) => calendar.primary);
  }, [fetchUserCalendarsQuery.data]);

  /**
   * 변경된 블록의 캘린더가 꺼져있는 경우 즉시 블록 전체 invalidate
   * @param calendarId - 변경된 블록의 캘린더 아이디
   */
  const handleChangeCalendarVisibility = (calendarId: number) => {
    const calendar = fetchUserCalendarsQuery.data?.find((calendar) => calendar.id === calendarId);
    if (!calendar || calendar.show) return;

    debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
      shouldInvalidateImmediately: true,
    });
    debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
      shouldInvalidateImmediately: true,
    });

    // 캘린더 show가 변경되었기 떄문에 invalidate
    queryClient.invalidateQueries({ queryKey: userCalendarsQueryKeys.all() });
  };

  /**
   * 캘린더 데이터 업데이트(낙관적 업데이트를 위한 로직)
   *
   * @param block - 업데이트할 블록 객체
   * @returns 업데이트된 블록 객체
   */
  const convertCalendarDataUpdateBlock = (block: BlockType) => {
    // start, end가 null 인 경우 calendarId, calendar null로 업데이트
    if (hasNoStartOrEnd(block)) {
      return {
        ...block,
        calendarId: null,
        calendar: null,
      };
    }

    // primaryCalendar로 업데이트 해야하는 경우(calendarId가 null인 경우)
    if (block.calendarId === null) {
      return {
        ...block,
        calendarId: primaryCalendar?.id,
        calendar: primaryCalendar,
      };
    }

    // calendarId민 있는 경우 calendar 데이터도 추가
    if (block.calendarId && !block.calendar) {
      return {
        ...block,
        calendarId: block.calendarId,
        calendar: fetchUserCalendarsQuery.data?.find(
          (calendar) => calendar.id === block.calendarId
        ),
      };
    }

    return block;
  };

  return { handleChangeCalendarVisibility, convertCalendarDataUpdateBlock };
};

export default useBlockCalendarSync;
